import { Sparkles, CalendarRange, Users, DollarSign, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { Heading } from "@/components/ui/heading";

export function FeaturesDeepDive() {
  const { t } = useTranslation();
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.05 });

  const featureCards = [
    {
      icon: Sparkles,
      title: t("featuresDeep.aiPlannerTitle"),
      description: t("featuresDeep.aiPlannerDesc"),
      highlights: [
        t("featuresDeep.aiHighlight1"),
        t("featuresDeep.aiHighlight2"),
        t("featuresDeep.aiHighlight3"),
        t("featuresDeep.aiHighlight4"),
      ],
      accentVar: "--brand-gold",
    },
    {
      icon: CalendarRange,
      title: t("featuresDeep.calendarTitle"),
      description: t("featuresDeep.calendarDesc"),
      highlights: [
        t("featuresDeep.calHighlight1"),
        t("featuresDeep.calHighlight2"),
        t("featuresDeep.calHighlight3"),
        t("featuresDeep.calHighlight4"),
      ],
      accentVar: "--brand-coral",
    },
    {
      icon: Users,
      title: t("featuresDeep.socialTitle"),
      description: t("featuresDeep.socialDesc"),
      highlights: [
        t("featuresDeep.socialHighlight1"),
        t("featuresDeep.socialHighlight2"),
        t("featuresDeep.socialHighlight3"),
        t("featuresDeep.socialHighlight4"),
      ],
      accentVar: "--brand-teal",
    },
    {
      icon: DollarSign,
      title: t("featuresDeep.expenseTitle"),
      description: t("featuresDeep.expenseDesc"),
      highlights: [
        t("featuresDeep.autoSplit"),
        t("featuresDeep.multiPayment"),
        t("featuresDeep.receipt"),
        t("featuresDeep.reminders"),
      ],
      accentVar: "--brand-gold",
    },
  ];

  const moreFeatures = [
    t("featuresDeep.moreFeature1"),
    t("featuresDeep.moreFeature2"),
    t("featuresDeep.moreFeature3"),
    t("featuresDeep.moreFeature4"),
    t("featuresDeep.moreFeature5"),
    t("featuresDeep.moreFeature6"),
  ];

  const heroCard = featureCards[0];
  const HeroIcon = heroCard.icon;
  const gridCards = featureCards.slice(1);

  return (
    <section
      ref={ref}
      id="features"
      className="py-24 md:py-32 scroll-mt-20"
      data-testid="section-features"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16">
          <Heading
            level={1}
            as="h2"
            serif
            className="text-4xl md:text-5xl lg:text-6xl font-black mb-5"
            data-testid="text-features-title"
          >
            {t("featuresDeep.sectionTitle")}
          </Heading>
          <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("featuresDeep.sectionSubtitle")}
          </p>
        </div>

        {/* Hero card: AI Trip Planner — full width, dramatic */}
        <div
          className={`relative rounded-2xl overflow-hidden p-8 md:p-12 lg:p-14 mb-6 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          } bg-gradient-card`}
          data-testid="card-feature-0"
        >
          {/* Grain */}
          <div className="absolute inset-0 grain-overlay pointer-events-none" />

          <div className="relative flex flex-col md:flex-row md:items-start gap-8">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `hsl(var(${heroCard.accentVar}) / 0.18)` }}
            >
              <HeroIcon className="h-7 w-7" style={{ color: `hsl(var(${heroCard.accentVar}))` }} />
            </div>
            <div className="flex-1">
              <Heading level={2} as="h3" serif className="text-2xl md:text-3xl lg:text-4xl font-black text-white mb-4">
                {heroCard.title}
              </Heading>
              <p className="text-primary-foreground/80 mb-8 max-w-2xl text-base lg:text-lg leading-relaxed">
                {heroCard.description}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {heroCard.highlights.map((highlight, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 group"
                  >
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                      style={{ backgroundColor: `hsl(var(${heroCard.accentVar}) / 0.18)` }}
                    >
                      <CheckCircle2 className="h-3 w-3" style={{ color: `hsl(var(${heroCard.accentVar}))` }} />
                    </div>
                    <span className="text-sm text-white/80">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bento grid: remaining 5 cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {gridCards.map((feature, idx) => {
            const Icon = feature.icon;
            const isLast = idx === gridCards.length - 1 && gridCards.length % 2 === 1;
            return (
              <div
                key={idx}
                className={`group relative rounded-2xl border border-border bg-card p-7 md:p-8 transition-all duration-500 hover:border-border/80 ${
                  isLast ? "md:col-span-2" : ""
                } ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{ transitionDelay: `${(idx + 1) * 100}ms` }}
                data-testid={`card-feature-${idx + 1}`}
              >
                {/* Hover glow effect */}
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    boxShadow: `0 0 40px -15px hsl(var(${feature.accentVar}) / 0.30)`,
                  }}
                />

                <div className={`${isLast ? "md:max-w-3xl md:mx-auto md:text-center" : ""}`}>
                  <div className={`flex items-center gap-3 mb-5 ${isLast ? "md:justify-center" : ""}`}>
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}
                      style={{ backgroundColor: `hsl(var(${feature.accentVar}) / 0.12)` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: `hsl(var(${feature.accentVar}))` }} />
                    </div>
                  </div>
                  <Heading level={4} as="h3" className="text-lg font-bold mb-2">
                    {feature.title}
                  </Heading>
                  <p className={`text-sm text-muted-foreground mb-5 leading-relaxed ${isLast ? "md:max-w-2xl md:mx-auto" : ""}`}>
                    {feature.description}
                  </p>
                  <ul className={`space-y-2.5 ${isLast ? "md:grid md:grid-cols-2 md:gap-x-10 md:gap-y-3 md:space-y-0 md:text-left md:max-w-3xl md:mx-auto" : ""}`}>
                    {feature.highlights.map((highlight, hIdx) => (
                      <li key={hIdx} className="flex items-start gap-2.5">
                        <CheckCircle2
                          className="h-4 w-4 flex-shrink-0 mt-0.5"
                          style={{ color: `hsl(var(${feature.accentVar}) / 0.80)` }}
                        />
                        <span className="text-sm">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* More features — pill strip */}
        <div className="mt-20">
          <Heading
            level={3}
            as="h3"
            serif
            className="text-2xl md:text-3xl font-black mb-3 text-center"
            data-testid="text-more-features-title"
          >
            {t("featuresDeep.moreTitle")}
          </Heading>
          <p className="text-muted-foreground mb-10 text-center max-w-lg mx-auto">
            {t("featuresDeep.moreDesc")}
          </p>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {moreFeatures.map((feature, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium transition-all duration-300 hover:bg-muted hover:border-brand-gold/20 hover:shadow-sm group"
                data-testid={`text-more-feature-${index}`}
              >
                <span className="text-brand-gold font-bold transition-transform duration-300 group-hover:translate-x-0.5">&rarr;</span>
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
